import React, {useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {ReadAsType} from '../../incase-domain/common/fileReaderString';
import {getUploadFileReader} from '../../utils/utils';

export type FileResult = {
    reader: FileReader;
    file: File;
};

export type FileLoaderType = 'image' | 'video' | 'nbl';

interface BaseFileLoaderProps {
    onLoad: (files: FileResult[]) => void;
    type: FileLoaderType;
    fileTypeCheck?: (file: File) => boolean;
    multipleFiles?: boolean;
    accept: string;
    readAs?: ReadAsType;
    child: JSX.Element;
    inputRef?: React.RefObject<HTMLInputElement>;
}

function BaseFileLoader(props: BaseFileLoaderProps): JSX.Element {
    const {onLoad, accept, type, multipleFiles, child, inputRef, fileTypeCheck, readAs = 'dataurl'} = props;
    const {t} = useTranslation();
    const fileInput = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        if (!e.target.files) return;

        const uploadResults = getUploadFileReader(Array.from(e.target.files), readAs);

        if (!uploadResults) return;

        const resPromises: Promise<FileResult | null>[] = [];

        uploadResults.forEach((uploadResult) => {
            const {reader, file} = uploadResult;
            if (fileTypeCheck ? !fileTypeCheck(uploadResult.file) : !uploadResult.file.type.includes(type)) {
                toast.error(t('presEditPage.editSidebar.validation.fileType'), {toastId: 'fileTypeError-basefileloader'});
                return;
            }

            if (reader) {
                resPromises.push(
                    new Promise<FileResult | null>((resolve) => {
                        reader.onload = () => {
                            resolve({reader, file});
                        };

                        reader.onerror = () => {
                            resolve(null);
                        };
                    })
                );
            }
        });

        Promise.all(resPromises).then((results) => {
            const validFiles = results.filter((result): result is FileResult => result !== null);
            onLoad(validFiles);
        });
    };

    const handleClick = (): void => {
        if (fileInput.current) {
            fileInput.current.click();
        }
    };

    return (
        <div className="c-highlightedInputMedia">
            <label>
                <input
                    type="file"
                    multiple={multipleFiles}
                    accept={accept}
                    ref={(currRef) => {
                        (fileInput as React.MutableRefObject<HTMLInputElement | null>).current = currRef;
                        if (inputRef) {
                            (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = currRef;
                        }
                    }}
                    onChange={handleFileChange}
                />

                <div onClick={handleClick}>{child}</div>
            </label>
        </div>
    );
}

export default BaseFileLoader;
